import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import { useDashboardChannel } from '~/lib/socket/use-dashboard-channel'
import {
  addDays,
  formatRangeLabel,
  startOfWeekMonday,
  toIsoDate,
} from '~/lib/time/format'
import { csvFilenameFor, downloadCsv, rangeSnapshotToCsv } from './csv-export'
import { DateNav } from './date-nav'
import { FiltersControl } from './filters'
import { MonthGrid } from './month-grid'
import { SidePanel } from './side-panel'
import { applyFilters, useCalendarFilters } from './use-filters'
import { useInvalidateRange, useRangeSnapshot } from './use-range-snapshot'
import { ViewToggle, type CalendarView } from './view-toggle'
import { WeekGrid } from './week-grid'
import styles from './calendar-page.module.scss'

const VIEW_PARAM = 'view'
const FROM_PARAM = 'from'
const PANEL_PARAM = 'panel'

export function CalendarPage() {
  const [params, setParams] = useSearchParams()
  const { data: me } = useCurrentUser()
  const tz = me?.user.timezone ?? 'America/Chicago'

  const view = readView(params.get(VIEW_PARAM))
  const todayIso = toIsoDate(new Date())
  const fromIso = params.get(FROM_PARAM) ?? defaultFrom(view, todayIso)
  const toIso = computeTo(view, fromIso)
  const isAtToday = fromIso === defaultFrom(view, todayIso)

  const range = useRangeSnapshot(fromIso, toIso, tz)
  const { filters, activeCount } = useCalendarFilters()

  const filteredRange = useMemo(
    () => (range.data ? applyFilters(range.data, filters) : range.data),
    [range.data, filters]
  )

  const noUsersAfterFilter =
    !!filteredRange && filteredRange.users.length === 0 && activeCount > 0

  // Real-time updates: any check-in / OOO / holiday / parse event from
  // the dashboard:snapshot channel invalidates the current visible range
  // query and triggers a refetch. The channel is a refcounted singleton
  // shared with Today, so this is just an additional listener.
  const invalidate = useInvalidateRange()
  const onSocketChange = useCallback(() => {
    invalidate(fromIso, toIso, tz)
  }, [invalidate, fromIso, toIso, tz])
  useDashboardChannel(me?.socket_token ?? null, onSocketChange)

  const label = useMemo(
    () => formatRangeLabel(fromIso, toIso),
    [fromIso, toIso]
  )

  const setView = (next: CalendarView) => {
    const newParams = new URLSearchParams(params)
    newParams.set(VIEW_PARAM, next)
    newParams.set(FROM_PARAM, defaultFrom(next, toIsoDate(new Date())))
    setParams(newParams, { replace: true })
  }

  const navigate = (delta: number) => {
    const stride = view === 'week' ? 7 : 30
    const nextFrom = addDays(fromIso, stride * delta)
    const newParams = new URLSearchParams(params)
    newParams.set(FROM_PARAM, nextFrom)
    setParams(newParams, { replace: true })
  }

  const goToday = () => {
    const newParams = new URLSearchParams(params)
    newParams.set(FROM_PARAM, defaultFrom(view, toIsoDate(new Date())))
    setParams(newParams, { replace: true })
  }

  const panelCheckinId = params.get(PANEL_PARAM)
  const openPanel = (checkinId: string) => {
    const newParams = new URLSearchParams(params)
    newParams.set(PANEL_PARAM, checkinId)
    setParams(newParams, { replace: false })
  }
  const closePanel = () => {
    const newParams = new URLSearchParams(params)
    newParams.delete(PANEL_PARAM)
    setParams(newParams, { replace: false })
  }

  const panelTarget = useMemo(() => {
    if (!panelCheckinId || !range.data) return null
    const matches = range.data.days
      .flatMap(day =>
        day.cells
          .filter(
            (
              c
            ): c is Extract<
              typeof c,
              { kind: 'working' } | { kind: 'parse_failed' }
            > =>
              (c.kind === 'working' || c.kind === 'parse_failed') &&
              c.checkin_id === panelCheckinId
          )
          .map(cell => ({ cell, date: day.date }))
      )
      .slice(0, 1)
    const hit = matches[0]
    if (!hit) return null
    const user = range.data.users.find(u => u.id === hit.cell.user_id)
    if (!user) return null
    return { cell: hit.cell, user, date: hit.date }
  }, [panelCheckinId, range.data])

  return (
    <div className={styles.page}>
      <div className={styles.chromeRow}>
        <h1 className={styles.heading}>Calendar</h1>
        <div className={styles.chromeRight}>
          <ViewToggle value={view} onChange={setView} />
          <FiltersControl users={range.data?.users ?? []} />
          <button
            type="button"
            className={styles.exportBtn}
            onClick={() => {
              if (!filteredRange) return
              downloadCsv(
                rangeSnapshotToCsv(filteredRange),
                csvFilenameFor(filteredRange)
              )
            }}
            disabled={!filteredRange || filteredRange.users.length === 0}
            aria-label="Download CSV"
          >
            CSV
          </button>
        </div>
      </div>
      <div className={styles.chromeRow}>
        <DateNav
          label={label}
          onPrev={() => navigate(-1)}
          onNext={() => navigate(1)}
          onToday={goToday}
          isAtToday={isAtToday}
        />
      </div>
      <main className={styles.body}>
        {range.isLoading && (
          <p className={styles.placeholder} aria-live="polite">
            Loading calendar…
          </p>
        )}
        {!range.isLoading &&
          filteredRange &&
          !noUsersAfterFilter &&
          view === 'week' && (
            <WeekGrid data={filteredRange} onCellClick={openPanel} />
          )}
        {!range.isLoading &&
          filteredRange &&
          !noUsersAfterFilter &&
          view === 'month' && (
            <MonthGrid data={filteredRange} onCellClick={openPanel} />
          )}
        {!range.isLoading && noUsersAfterFilter && (
          <p className={styles.placeholder}>
            No engineers match these filters.
          </p>
        )}
        {range.isError && (
          <p className={styles.placeholder} role="alert">
            Couldn&apos;t load calendar.{' '}
            <button
              type="button"
              className={styles.retry}
              onClick={() => range.refetch()}
            >
              Retry →
            </button>
          </p>
        )}
      </main>
      {panelTarget && (
        <SidePanel
          cell={panelTarget.cell}
          user={panelTarget.user}
          date={panelTarget.date}
          onClose={closePanel}
        />
      )}
    </div>
  )
}

function readView(raw: string | null): CalendarView {
  return raw === 'month' ? 'month' : 'week'
}

function defaultFrom(view: CalendarView, todayIso: string): string {
  return view === 'week' ? startOfWeekMonday(todayIso) : todayIso
}

function computeTo(view: CalendarView, fromIso: string): string {
  // Week = 7 days inclusive (Mon–Sun); Month = 30 days inclusive (rolling).
  const span = view === 'week' ? 6 : 29
  return addDays(fromIso, span)
}
